import styled, { css } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  greyMui,
  purpleMui,
  redMui,
  ScreenDepth,
} from "@ryde/theme";

import { DRAWER_Z_INDEX_OFFSET } from "./ai-conversation.constants";
import {
  getStructureDepthStyles,
  getStructureKeyColorStyles,
} from "./ai-conversation.utils";

export const DetailsDrawerLayer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-conversation-details-drawer-layer",
})`
  position: fixed;
  inset: 0;
  z-index: ${ScreenDepth.LAYOUT_RIGHT_BACKDROP + DRAWER_Z_INDEX_OFFSET};
  pointer-events: auto;

  [data-e2e="layout-right-content"] {
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
  }
  [data-e2e="layout-right-crud-content"] {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  [data-e2e="form-content-static"] {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    margin-top: 0;
    gap: 0;
  }
`;

export const DetailsTabsContentWrap = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-conversation-details-tabs-content-wrap",
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  .details-tabs-header {
    flex-shrink: 0;
    padding-top: 12px;
    padding-bottom: 8px;
  }

  .details-search-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding-inline-end: 8px;
    flex-shrink: 0;
  }

  .details-search-row .MuiTextField-root {
    flex: 1;
    min-width: 0;
  }
  .details-search-row .MuiInputBase-root {
    font-size: 13px;
  }
  .details-search-row .MuiInputAdornment-root {
    margin-right: 2px;
  }
  .details-search-row .MuiIconButton-root {
    cursor: pointer;
    padding: 4px;
  }
  .details-search-row .MuiIconButton-root:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .details-search-row .details-search-row__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    min-width: 32px;
    padding: 0;
    border: 1px solid ${greyMui._300};
    background-color: ${greyMui._50};
    cursor: pointer;
    border-radius: 4px;
    color: ${greyMui._600};
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .details-search-row .details-search-row__btn:hover:not(:disabled) {
    background-color: ${greyMui._100};
    color: ${greyMui._800};
    border-color: ${greyMui._400};
  }

  .details-search-row .details-search-row__btn:active:not(:disabled) {
    background-color: ${greyMui._200};
  }

  .details-search-row .details-search-row__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .details-search-row .details-search-row__btn svg {
    width: 18px;
    height: 18px;
  }
`;

const DETAILS_TAB_CONTENT_BASE = css`
  padding: 10px 10px 40px 20px;
  padding-inline-start: 20px;
  line-height: 1.6;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const DetailsTabsScrollContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-conversation-details-tabs-scroll",
})`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .details-tab-content {
    ${DETAILS_TAB_CONTENT_BASE}
  }

  .details-tab-plain {
    ${DETAILS_TAB_CONTENT_BASE}
    padding: 4px 8px 4px 8px;
    padding-inline-start: 8px;
  }
  .details-tab-plain pre {
    padding: 4px 8px;
    margin-bottom: 12px;
  }

  .details-tab-full-info {
    ${DETAILS_TAB_CONTENT_BASE}
    display: flex;
    flex-direction: column;
    padding: 4px 8px 4px 8px;
    padding-inline-start: 8px;
    overflow: hidden;
  }
  .details-tab-full-info .full-info-tab-scroll-area {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: auto;
  }

  .details-tab-raw {
    ${DETAILS_TAB_CONTENT_BASE}
    padding: 4px 8px 4px 8px;
    padding-inline-start: 8px;
  }
  .details-tab-raw .raw-text-pre {
    margin: 0;
    padding: 8px;
    font-family:
      ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, monospace;
    font-size: 11px;
    line-height: 1.4;
    color: ${greyMui._700};
    white-space: pre-wrap;
    word-break: break-word;
    overflow-x: auto;
  }

  .details-tab-structure {
    ${DETAILS_TAB_CONTENT_BASE}
    padding: 4px 8px 4px 8px;
    padding-inline-start: 8px;
  }
  .structure-tree-line {
    padding: 2px 0;
    font-size: 13px;
    line-height: 1.5;
    font-family:
      ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, monospace;
  }
  ${getStructureDepthStyles()}
  ${getStructureKeyColorStyles()}
  .structure-key-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: help;
  }
  .structure-key {
    font-weight: 500;
  }
  .structure-emoji {
    font-size: 12px;
    opacity: 0.8;
  }
  .structure-type {
    margin-inline-start: 6px;
    font-size: 11px;
    font-weight: normal;
  }
  .structure-type.type-string {
    color: ${blueGreyMui._800};
  }
  .structure-type.type-number {
    color: ${blueGreyMui._600};
  }
  .structure-type.type-boolean {
    color: ${purpleMui._500};
  }
  .structure-type.type-array {
    color: ${greyMui._700};
    font-weight: 600;
  }
  .structure-type.type-null {
    color: ${redMui._400};
  }
  .structure-value {
    margin-inline-start: 8px;
    color: ${greyMui._700};
    font-size: 12px;
    font-style: italic;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    vertical-align: middle;
  }
`;

export const FullInfoTabToolbar = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-conversation-details-full-info-toolbar",
})`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
  padding-bottom: 4px;

  .MuiIconButton-root {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.08);
    border-radius: 50%;
    padding: 6px;
  }
  .MuiIconButton-root:hover {
    background-color: rgba(0, 0, 0, 0.12);
  }
`;

export const StructureTooltipContent = styled.pre.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-conversation-structure-tooltip-content",
})`
  margin: 0;
  padding: 8px 10px;
  font-family:
    ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, monospace;
  font-size: 11px;
  line-height: 1.4;
  color: ${greyMui._200};
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 400px;
  max-height: 300px;
  overflow: auto;
`;
